import type {
    AccessChildRef,
    AccessEntry,
    AccessMap,
    AccessNode,
    AccessPath,
    AccessPathStep,
    AssetNode,
    AssetType,
    FlatAccessEntry,
} from './access-graph.types';
import type { Group } from '@thinkalpha/access-ws-client/contracts/group.js';
import type { ScopedRole } from '@thinkalpha/access-ws-client/contracts/roles.js';
import type {
    DetailedFirmMembership,
    DetailedGroupMembership,
    DetailedUser,
    SecurityPackage,
} from '@thinkalpha/access-ws-client/contracts/user.js';
import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { ConcreteAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';

// --- Node ID helpers ---

export function userNodeId(userId: string): string {
    return `user:${userId}`;
}

export function firmNodeId(firmId: string): string {
    return `firm:${firmId}`;
}

export function groupNodeId(groupId: string): string {
    return `group:${groupId}`;
}

export function subscriptionNodeId(subscriptionId: string): string {
    return `subscription:${subscriptionId}`;
}

export function assetNodeId(assetType: AssetType, assetId: string): string {
    return `asset:${assetType}:${assetId}`;
}

export function routeNodeId(routeId: string): string {
    return `route:${routeId}`;
}

// --- Map construction helpers ---

function ensureEntry(map: AccessMap, node: AccessNode): AccessEntry {
    const existing = map.get(node.id);
    if (existing) return existing;
    const entry: AccessEntry = { ...node, children: [] };
    map.set(node.id, entry);
    return entry;
}

function addChild(map: AccessMap, parentId: string, child: AccessChildRef): void {
    const parent = map.get(parentId);
    if (parent) {
        parent.children.push(child);
    }
}

/**
 * Build a directed access map from resolved data.
 * Each entry holds its own children — no separate edge list needed.
 * Pure function — no side effects, no API calls.
 */
export function buildAccessMap(
    user: DetailedUser,
    securityPackage: SecurityPackage,
    firms: DetailedFirmMembership[],
    groups: DetailedGroupMembership[],
    accounts?: ConcreteAccount[],
): AccessMap {
    const map: AccessMap = new Map();
    const userId = userNodeId(user.id);

    // Root: user entry
    ensureEntry(map, {
        id: userId,
        type: 'user',
        label: `${user.firstName} ${user.lastName}`,
        meta: {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            roles: user.roles ?? [],
        },
    });

    // Firm entries + firm-membership children
    for (const firm of firms) {
        const fId = firmNodeId(firm.firmId);
        ensureEntry(map, {
            id: fId,
            type: 'firm',
            label: firm.name,
            meta: {
                firmId: firm.firmId,
                membershipId: firm.id,
                permission: firm.permission,
                appliedRoles: firm.appliedRoles,
                appliedGroupIds: firm.appliedGroups.map((g) => g.groupId),
            },
        });

        addChild(map, userId, {
            entryId: fId,
            reason: { type: 'firm-membership', permission: firm.permission },
        });

        // Firm-context groups: groups assigned to a specific firm member
        if (firm.groups) {
            for (const groupMembership of firm.groups) {
                const gId = groupNodeId(groupMembership.groupId);
                ensureGroupEntryPlaceholder(map, gId, groupMembership.groupId, groupMembership.permission);

                addChild(map, fId, {
                    entryId: gId,
                    reason: {
                        type: 'firm-context-group',
                        firmId: firm.firmId,
                        permission: groupMembership.permission,
                    },
                });
            }
        }

        // Applied groups: inherited by all firm members
        for (const groupMembership of firm.appliedGroups) {
            const gId = groupNodeId(groupMembership.groupId);
            ensureGroupEntryPlaceholder(map, gId, groupMembership.groupId, groupMembership.permission);

            addChild(map, fId, {
                entryId: gId,
                reason: {
                    type: 'applied-group',
                    firmId: firm.firmId,
                    permission: groupMembership.permission,
                },
            });
        }
    }

    // Direct group memberships (from user.groups)
    for (const group of user.groups) {
        const gId = groupNodeId(group.groupId);
        ensureGroupEntryPlaceholder(map, gId, group.groupId, group.permission);

        addChild(map, userId, {
            entryId: gId,
            reason: { type: 'direct-group-membership', permission: group.permission },
        });
    }

    // Subscription entries
    for (const subscription of securityPackage.subscriptions) {
        const sId = subscriptionNodeId(subscription.id);
        ensureEntry(map, {
            id: sId,
            type: 'subscription',
            label: subscription.plan.name,
            meta: {
                subscriptionId: subscription.id,
                planName: subscription.plan.name,
                status: subscription.status,
            },
        });

        addChild(map, userId, {
            entryId: sId,
            reason: { type: 'subscription', subscriptionId: subscription.id },
        });
    }

    // Account asset entries
    if (accounts) {
        for (const account of accounts) {
            const aId = assetNodeId('account', account.id);
            ensureEntry(map, {
                id: aId,
                type: 'asset',
                label: account.name,
                meta: {
                    assetType: 'account',
                    assetId: account.id,
                    permission: account.permissions.effectiveAccess,
                },
            });

            addChild(map, userId, {
                entryId: aId,
                reason: {
                    type: 'asset-access',
                    sourceType: 'user',
                    permission: account.permissions.effectiveAccess,
                },
            });
        }
    }

    // Enrich group entries with resolved group detail data
    enrichGroupEntries(map, groups);

    return map;
}

/**
 * Ensure a group entry exists with at least placeholder data.
 * If the entry already exists, leave it as-is.
 */
function ensureGroupEntryPlaceholder(
    map: AccessMap,
    entryId: string,
    groupId: string,
    permission: PermissionType | null,
): void {
    if (map.has(entryId)) return;

    ensureEntry(map, {
        id: entryId,
        type: 'group',
        label: groupId, // Placeholder — enriched later when groups resolve
        meta: {
            groupId,
            permission: permission,
            roles: [],
        },
    });
}

/**
 * Patch group entry labels and metadata from resolved group details.
 * The map is structurally correct before enrichment; only display labels and meta are affected.
 */
export function enrichGroupEntries(map: AccessMap, resolvedGroups: Array<Group | DetailedGroupMembership>): void {
    for (const group of resolvedGroups) {
        // DetailedGroupMembership has both `id` (Group.id) and `groupId`
        const id = 'groupId' in group ? group.groupId : group.id;
        const entryId = groupNodeId(id);
        const existing = map.get(entryId);

        if (existing && existing.type === 'group') {
            existing.label = group.name;
            existing.meta.roles = group.roles;
            if (group.firmId) {
                existing.meta.firmId = group.firmId;
            }
        }
    }
}

// --- Selectors ---

/**
 * Find all provenance paths from the root user entry to the target entry.
 * Traverses each entry's children to enumerate all paths.
 */
export function provenanceOf(map: AccessMap, targetEntryId: string): AccessPath[] {
    const rootId = findRootUserId(map);
    if (!rootId || rootId === targetEntryId) return [];

    const paths: AccessPath[] = [];

    function dfs(currentId: string, currentPath: AccessPathStep[]): void {
        if (currentId === targetEntryId) {
            paths.push([...currentPath]);
            return;
        }

        const entry = map.get(currentId);
        if (!entry) return;

        for (const child of entry.children) {
            // Cycle guard
            if (currentPath.some((step) => step.to === child.entryId)) continue;
            currentPath.push({ from: currentId, to: child.entryId, reason: child.reason });
            dfs(child.entryId, currentPath);
            currentPath.pop();
        }
    }

    dfs(rootId, []);
    return paths;
}

/**
 * Find all asset entries reachable (directly or transitively) from a source entry.
 */
export function assetsBySource(map: AccessMap, sourceEntryId: string): AssetNode[] {
    const visited = new Set<string>();
    const result: AssetNode[] = [];

    function dfs(currentId: string): void {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const entry = map.get(currentId);
        if (!entry) return;

        if (entry.type === 'asset') {
            result.push(entry);
        }

        for (const child of entry.children) {
            dfs(child.entryId);
        }
    }

    dfs(sourceEntryId);
    return result;
}

/**
 * Build a flat access list: one entry per non-user node (firms, groups, subscriptions, assets),
 * with all provenance paths from root to that entry and resolved roles.
 * User-sourced roles are implicitly included — the user entry appears in every provenance path,
 * so any role with a user source will match on every entry.
 */
export function buildFlatAccessList(map: AccessMap, securityPackage: SecurityPackage): FlatAccessEntry[] {
    // Build a plan-to-subscription lookup so plan-sourced roles resolve to the correct subscription entry
    const planToSubscriptionEntryId = new Map<string, string>();
    for (const sub of securityPackage.subscriptions) {
        if ('planId' in sub) {
            planToSubscriptionEntryId.set(sub.planId, subscriptionNodeId(sub.id));
        }
    }

    // Build a membership-to-firm lookup so firm-membership-sourced roles resolve to the correct firm entry
    const membershipToFirm = new Map<string, string>();
    for (const [, entry] of map) {
        if (entry.type === 'firm' && entry.meta.membershipId) {
            membershipToFirm.set(entry.meta.membershipId, entry.meta.firmId);
        }
    }

    const entries: FlatAccessEntry[] = [];

    for (const [entryId, entry] of map) {
        if (entry.type === 'user') continue;

        const paths = provenanceOf(map, entryId);
        if (paths.length === 0) continue;

        const resolvedRoles = resolveRolesForEntry(
            entryId,
            paths,
            securityPackage.roles,
            planToSubscriptionEntryId,
            membershipToFirm,
        );
        entries.push({ node: entry, paths, resolvedRoles });
    }

    return entries;
}

/**
 * Given an entry and its provenance paths, resolve which ScopedRoles apply.
 * Maps each ScopedRole source to a fully-qualified entry ID (via resolveAccessListSourceNode)
 * and checks membership against the set of entry IDs in the provenance paths.
 * Plan-sourced roles are resolved via the planToSubscriptionEntryId lookup.
 */
function resolveRolesForEntry(
    entryId: string,
    paths: AccessPath[],
    scopedRoles: ScopedRole[],
    planToSubscriptionEntryId: Map<string, string>,
    membershipToFirm: Map<string, string>,
): ScopedRole[] {
    // Collect all fully-qualified entry IDs in provenance paths (including the target entry itself)
    const entriesInPaths = new Set<string>();
    entriesInPaths.add(entryId);
    for (const path of paths) {
        for (const step of path) {
            entriesInPaths.add(step.from);
            entriesInPaths.add(step.to);
        }
    }

    return scopedRoles.filter((sr) =>
        sr.sources.some((source) => {
            let qualifiedId: string | null;
            if (source.type === 'plan') {
                qualifiedId = planToSubscriptionEntryId.get(source.id) ?? null;
            } else {
                qualifiedId = resolveAccessListSourceNode(source.type, source.id, membershipToFirm);
            }
            return qualifiedId !== null && entriesInPaths.has(qualifiedId);
        }),
    );
}

// --- Resolve access list source node ---

/**
 * Map an Access entry's source to an existing graph node ID.
 */
export function resolveAccessListSourceNode(
    sourceType: string,
    sourceId: string,
    membershipToFirm?: Map<string, string>,
): string | null {
    switch (sourceType) {
        case 'user':
            return userNodeId(sourceId);
        case 'firm':
            return firmNodeId(sourceId);
        case 'firm-membership': {
            const firmId = membershipToFirm?.get(sourceId);
            return firmId ? firmNodeId(firmId) : null;
        }
        case 'group':
            return groupNodeId(sourceId);
        case 'subscription':
            return subscriptionNodeId(sourceId);
        default:
            return null;
    }
}

// --- Graph utilities ---

function findRootUserId(map: AccessMap): string | null {
    for (const [id, entry] of map) {
        if (entry.type === 'user') return id;
    }
    return null;
}
